import { useCallback, useEffect, useMemo } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import { useNavigate } from 'hooks/useNavigate';
import { Col, Form, Row } from 'antd';
import { useApi } from 'hooks/useApi';
import { useTranslation } from 'react-i18next';
import LeasingPlansApi from 'libs/api/leasing/plans';
import { useFilter } from 'hooks/useFilter';
import UIHelper from 'helpers/UIHelper';
import ErrorHelper from 'helpers/ErrorHelper';
import moment from 'moment';
import { Button } from 'components';
import { DeleteIcon, EditIcon } from 'icons';
import { EyeOutlined } from '@ant-design/icons';
import { useRecoilValue } from 'recoil';
import { getPage } from 'hooks/useUser';
import FileTypeButton from '../../../../components/fileTypeButton';
import Helpers from '../../../../helpers';

const api = new LeasingPlansApi();

export const useLeasingPlans = (isMainPage = true) => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const { filterData, filterLoading } = useFilter();
  const { id } = useParams();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const pages = useRecoilValue(getPage);

  const {
    loading,
    data,
    singleItem,
    onDelete,
    onChangeTableFilter,
    updateInProcess,
    setUpdateInProcess,
    handleDownloadExcel,
  } = useApi({
    resourceKey: 'plans',
    singleResourceKey: 'plan',
    api,
    isMainPage,
  });

  const handleCreateOrUpdate = useCallback(
    async (values) => {
      setUpdateInProcess(true);
      try {
        const formData = new FormData();
        Object.keys(values).forEach((key) => {
          if (key === 'day') {
            formData.append(
              `plan[${key}]`,
              moment(values[key]).format('YYYY-MM-DD'),
            );
          }
          if (key === 'file') {
            console.log('value', values[key]);
            if (values[key]) {
              formData.append(`plan[${key}]`, values[key][0]?.originFileObj);
            }
          } else {
            formData.append(`plan[${key}]`, values[key]);
          }
        });
        if (!id) {
          await api.create({ data: formData });
          navigate(UIHelper.getListRoute(pathname, 1));
        } else {
          await api.put(id, { data: formData });
          navigate(UIHelper.getListRoute(pathname));
        }
      } catch (e) {
        const errors = e.response.data || [];
        form.setFields(ErrorHelper.getErrorsForForm(errors));
      } finally {
        setUpdateInProcess(false);
      }
    },
    [id, navigate, form, pathname, setUpdateInProcess],
  );

  useEffect(() => {
    if (id && !isMainPage && singleItem?.title) {
      form.setFields([
        {
          name: 'title',
          value: singleItem.title,
        },
        {
          name: 'leasings_asset_id',
          value: singleItem.leasings_asset_id,
        },
        {
          name: 'day',
          value: moment(singleItem.day, 'YYYY-MM-DD'),
        },
        {
          name: 'file',
          value: singleItem.file_name,
        },
      ]);
    }
  }, [singleItem, form, id, isMainPage]);

  const handleDownload = useCallback(
    async (url, fileName = 'vacancy_application.pdf') => {
      setUpdateInProcess(true);
      try {
        const response = await api.getFile(url);
        Helpers.downloadFile(response, fileName);
      } catch (e) {
        console.log(e);
        UIHelper.serverError();
      } finally {
        setUpdateInProcess(false);
      }
    },
    [setUpdateInProcess],
  );

  const columns = useMemo(() => {
    return [
      {
        title: t('Asset'),
        dataIndex: 'leasings_assets.title',
        // fixed: 'left',
        sorter: true,
        render: (data, row) => row?.leasings_asset,
      },
      {
        title: t('Titolo'),
        dataIndex: 'title',
        sorter: true,
      },
      {
        title: t('Data'),
        dataIndex: 'created_at',
        render: (r) => UIHelper.formatedDate(r),
        sorter: true,
      },
      {
        title: t('Caricato'),
        dataIndex: 'day',
        render: (r) => UIHelper.formatedDate(r),
        sorter: true,
      },
      {
        title: t('File'),
        dataIndex: 'file',
        render: (data, row) => {
          const hasFile = !!row.file?.file_url;
          return hasFile ? (
            <a
              href={row.file_url}
              target='_blank'
              rel='noopener noreferrer'
              onClick={(e) => {
                e.preventDefault();
                handleDownload(row.file?.file_url, row.file?.file_name);
              }}
            >
              <FileTypeButton row={row} data={data} />
            </a>
          ) : null;
        },
      },
      {
        title: '',
        dataIndex: '__',
        align: 'right',
        width: 160,
        fixed: 'right',
        render: (data, row) =>
          (pages?.ability?.can_read !== false ||
            row.can_read !== false ||
            row.can_update !== false ||
            row.can_destroy !== false) && (
            <Row
              gutter={16}
              justify={'end'}
              onClick={(e) => e.stopPropagation()}
            >
              {(pages?.ability?.can_read !== false ||
                row.can_read !== false) && (
                <Col>
                  <Link to={`${pathname}/${row.id}`}>
                    <Button type={'secondary'} icon={<EyeOutlined />} />
                  </Link>
                </Col>
              )}
              {row.can_update !== false && (
                <Col>
                  <Link to={`${pathname}/${row.id}/edit`}>
                    <Button type={'secondary'} icon={<EditIcon />} />
                  </Link>
                </Col>
              )}
              {row.can_destroy !== false && (
                <Col>
                  <Link to={`${pathname}/${row.id}/delete`}>
                    <Button type={'red'} icon={<DeleteIcon />} />
                  </Link>
                </Col>
              )}
            </Row>
          ),
      },
    ];
  }, [pathname, t, handleDownload, pages?.ability?.can_read]);

  const filters = useMemo(
    () => [
      {
        title: t('Titolo'),
        type: 'input',
        name: 'by_title',
      },
      {
        title: t('Asset'),
        type: 'select',
        name: 'by_leasings_asset_id',
        values: (filterData.leasings_assets || []).map((i) => ({
          title: i.title,
          value: i.value,
        })),
      },
      {
        title: t('Date range'),
        type: 'range',
        name: 'dateRange',
        keys: ['by_day_from', 'by_day_to'],
      },
    ],
    [t, filterData],
  );

  return {
    loading,
    data,
    singleItem,
    assets: filterData.leasings_assets || [],
    onDelete,
    onChangeTableFilter,
    updateInProcess,
    setUpdateInProcess,
    handleDownloadExcel,
    handleDownload,
    handleCreateOrUpdate,
    form,
    columns,
    filters,
    filterLoading,
  };
};
